/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import AppTable from 'components/appTable';
import Empty from 'components/empty';
import SearchBox from 'components/form/searchBox';
import AppSwitch from 'components/form/switch';
import PanelBoxLayout from 'components/layout/panelBox';
import Toolbar from 'components/toolbar';
import PaginationApp from 'components/pagination';
import RowAction from 'components/rowAction';
import Value from 'components/value';
import { MISSING } from 'common/helpers/format';
import ModalApp from 'components/modalApp';
import AssignTeaboyForm from '../assignTeaboyForm';
import { useActions } from 'common/hooks/useActions';

import * as S from './styles';
import * as T from 'common/types';

interface IProps {
  data: { list: T.Pages.FloorListItem[]; totalPages: number };
  teaboyList: T.Pages.TeaboyListItem[];
  loading: boolean;
}

const PenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const FloorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-5 9 5-9 5-9-5z" />
    <path d="M3 15l9 5 9-5" />
  </svg>
);

export default function CafeteriaTeaboyManagementTable({ data, teaboyList, loading }: IProps) {
  const { floorsRequestAction, cafeteriaStatusRequestAction } = useActions();
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState<T.Pages.FloorListItem | null>(null);
  const [meta, setMeta] = useState({
    page: 1,
    take: 10,
    search: '',
    getTeaBoys: true
  });

  const fetchData = () => {
    floorsRequestAction(meta);
  };

  useEffect(() => {
    fetchData();
  }, [meta]);

  const handleShowEdit = (item: T.Pages.FloorListItem) => {
    setShowEditModal(true);
    setEditData(item);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditData(null);
  };

  const handleChangePage = (page: number) => {
    setMeta({ ...meta, page });
  };

  const handleChangeTake = (take: number) => {
    setMeta({ ...meta, take });
  };

  const onChangeStatus = (status: boolean, id: number) => {
    cafeteriaStatusRequestAction(
      {
        id,
        status
      },
      () => {
        fetchData();
      }
    );
  };

  return (
    <S.Content>
      <PanelBoxLayout className="flush" contentStyle={{ padding: 0 }}>
        <Toolbar>
          <SearchBox
            onChange={(e) => setMeta({ ...meta, search: e, page: 1 })}
            placeholder="Search floors"
            value={meta.search}
          />
        </Toolbar>
        <AppTable
          headers={[
            { id: 1, key: 'id', title: 'Floor', dir: 'left', width: 110 },
            { id: 2, key: 'user', title: 'Tea boy', dir: 'left' },
            { id: 3, key: 'status', title: 'Cafeteria', dir: 'left', width: 150 },
            { id: 4, key: 'management', title: '', type: 'actions', width: 64 }
          ]}
          loading={loading}
          data={data.list}
          empty={
            <Empty
              icon={<FloorIcon />}
              title={meta.search ? `No floors match “${meta.search}”` : 'No floors yet'}
              text={
                meta.search
                  ? 'Check the spelling, or clear the search to see every floor.'
                  : 'Add a floor under Location Management, then assign a tea boy to it here.'
              }
            />
          }
          altanfeethiFields={{
            id: (item: T.Pages.FloorListItem) => (
              <S.Div className="text-left">
                <Value>{item?.name}</Value>
              </S.Div>
            ),
            /*
             * A floor with nobody on it used to render an empty cell, which is
             * the same thing an unloaded row looks like. It says so now, and
             * the row's action reads Assign rather than Edit.
             */
            user: (item: T.Pages.FloorListItem) => (
              <S.Div className="text-left captal">
                <Value fallback={MISSING.assigned}>{item?.teaBoy?.user?.displayName}</Value>
              </S.Div>
            ),
            /*
             * The bare antd `Switch` was the one control on the screen still
             * painted antd blue. `components/form/switch` is the design's —
             * `--line-strong` off, `--ok` on — and it carries the state in
             * words beside itself, because a lone toggle in a column says
             * nothing about what it toggles.
             *
             * It is disabled with no tea boy on the floor, which is the
             * existing rule: there is nobody to take the orders.
             */
            status: (item: T.Pages.FloorListItem) => (
              <S.Div className="text-left">
                <AppSwitch
                  theme="two"
                  checked={!!item?.cafeteriaStatus}
                  text={item?.cafeteriaStatus ? 'Open' : 'Closed'}
                  onChange={(val: boolean) => onChangeStatus(val, item.id)}
                  disabled={!item?.teaBoy?.id}
                />
              </S.Div>
            ),
            management: (item: T.Pages.FloorListItem) => (
              <RowAction
                onClick={() => handleShowEdit(item)}
                label={item?.teaBoy?.id ? `Change the tea boy on ${item?.name}` : `Assign a tea boy to ${item?.name}`}
              >
                {item?.teaBoy?.id ? <PenIcon /> : <PlusIcon />}
              </RowAction>
            )
          }}
        />
      </PanelBoxLayout>
      {data.totalPages ? (
        <PaginationApp
          page={meta.page}
          count={meta.take * data.totalPages}
          handleChangePage={(page: number) => handleChangePage(page)}
          key={data.totalPages}
          pageSize={meta.take}
          handleChangeTake={handleChangeTake}
        />
      ) : (
        <></>
      )}

      <ModalApp
        show={showEditModal}
        fixed
        handleClose={handleCloseEdit}
        title={`Tea boy on floor ${editData?.name ?? ''}`.trim()}
        style={{ maxWidth: 400 }}
      >
        <AssignTeaboyForm
          data={editData as T.Pages.FloorListItem}
          teaboyList={teaboyList}
          closeModal={handleCloseEdit}
          fetchData={fetchData}
        />
      </ModalApp>
    </S.Content>
  );
}
